"use client";

import { SiteNav, SiteFooter } from "./chrome";
import { STYLES as BASE_STYLES } from "./LandingV2";
import {
  COMPARE_TARGETS,
  type CompareTarget,
  type CompareFeatureRow,
} from "@/lib/compare/targets";

const REGISTER_URL = "https://dashboard.lvlup-app.com/auth/register";

export const COMPARE_STYLES = `
.lvlup-landing .cp-hero { position: relative; overflow: hidden; padding: 80px 0 60px; border-bottom: 1px solid var(--line); text-align: center; }
.lvlup-landing .cp-hero-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(var(--line) 1px, transparent 1px),
    linear-gradient(90deg, var(--line) 1px, transparent 1px);
  background-size: 64px 64px;
  -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 80%);
          mask-image: radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 80%);
  pointer-events: none;
}
.lvlup-landing .cp-hero-glow {
  position: absolute; top: -260px; left: 50%; transform: translateX(-50%);
  width: 800px; height: 600px;
  background: radial-gradient(ellipse at center, oklch(0.88 0.18 120 / 0.10), transparent 60%);
  pointer-events: none;
}
.lvlup-landing .cp-hero-inner { position: relative; max-width: 860px; margin: 0 auto; }
.lvlup-landing .cp-hero h1 {
  font-family: var(--display);
  font-size: clamp(40px, 6vw, 72px);
  font-weight: 700; letter-spacing: -2px; line-height: 1;
  margin: 20px 0;
  text-wrap: balance;
  color: var(--ink);
}
.lvlup-landing .cp-hero h1 .lp-accent { color: var(--accent); }
.lvlup-landing .cp-hero p.cp-lead {
  font-size: 18px; color: var(--ink-dim);
  max-width: 640px; margin: 0 auto 24px;
  text-wrap: pretty;
}
.lvlup-landing .cp-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.lvlup-landing .cp-vs {
  display: inline-flex; align-items: center; gap: 14px;
  font-family: var(--mono); font-size: 12px; color: var(--ink-faint);
  letter-spacing: 0.6px; text-transform: uppercase;
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 999px; padding: 8px 18px;
}
.lvlup-landing .cp-vs strong { color: var(--ink); font-weight: 700; }
.lvlup-landing .cp-vs .cp-vs-x { color: var(--accent); font-weight: 700; }

.lvlup-landing .cp-section { padding: 80px 0; border-bottom: 1px solid var(--line); }
@media (max-width: 720px) { .lvlup-landing .cp-section { padding: 56px 0; } }
.lvlup-landing .cp-section-head { text-align: center; max-width: 720px; margin: 0 auto 48px; }
.lvlup-landing .cp-section-head h2 {
  font-family: var(--display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700; letter-spacing: -1.2px; line-height: 1.05;
  margin: 12px 0; text-wrap: balance; color: var(--ink);
}
.lvlup-landing .cp-section-head h2 .lp-accent { color: var(--accent); }
.lvlup-landing .cp-section-head p { font-size: 16px; color: var(--ink-dim); margin: 0; text-wrap: pretty; }

/* Hub grid */
.lvlup-landing .cp-hub-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
@media (max-width: 720px) { .lvlup-landing .cp-hub-grid { grid-template-columns: 1fr; } }
.lvlup-landing .cp-hub-card {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 20px; padding: 28px;
  display: flex; flex-direction: column; gap: 14px;
  transition: border-color 0.2s, transform 0.2s;
  color: inherit;
}
.lvlup-landing .cp-hub-card:hover { border-color: var(--accent-line); transform: translateY(-3px); }
.lvlup-landing .cp-hub-tag {
  font-family: var(--mono); font-size: 10px; color: var(--accent);
  letter-spacing: 0.6px; text-transform: uppercase;
}
.lvlup-landing .cp-hub-card h3 { font-family: var(--display); font-size: 22px; font-weight: 700; letter-spacing: -0.5px; margin: 0; color: var(--ink); }
.lvlup-landing .cp-hub-card p { font-size: 14px; color: var(--ink-dim); margin: 0; line-height: 1.5; text-wrap: pretty; }
.lvlup-landing .cp-hub-cta { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 11px; color: var(--accent); letter-spacing: 0.5px; text-transform: uppercase; margin-top: auto; }

/* Reasons grid */
.lvlup-landing .cp-reasons { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
@media (max-width: 720px) { .lvlup-landing .cp-reasons { grid-template-columns: 1fr; } }
.lvlup-landing .cp-reason {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 16px; padding: 24px;
}
.lvlup-landing .cp-reason h3 { font-family: var(--display); font-size: 18px; font-weight: 600; letter-spacing: -0.3px; margin: 0 0 8px; color: var(--ink); }
.lvlup-landing .cp-reason p { font-size: 14px; color: var(--ink-dim); margin: 0; line-height: 1.55; text-wrap: pretty; }

/* Comparison table */
.lvlup-landing .cp-table-wrap { background: var(--bg-raised); border: 1px solid var(--line); border-radius: 18px; overflow: hidden; }
.lvlup-landing .cp-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.lvlup-landing .cp-table thead th {
  text-align: left; padding: 18px 22px;
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.5px;
  color: var(--ink-faint); text-transform: uppercase;
  background: var(--bg-deep);
  border-bottom: 1px solid var(--line);
}
.lvlup-landing .cp-table thead th.cp-th-us { color: var(--accent); }
.lvlup-landing .cp-table tbody td {
  padding: 16px 22px;
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  vertical-align: middle;
}
.lvlup-landing .cp-table tbody tr:last-child td { border-bottom: none; }
.lvlup-landing .cp-table tbody td:first-child { font-weight: 500; color: var(--ink); }
.lvlup-landing .cp-table .cp-yes { color: var(--accent); font-weight: 700; display: inline-flex; align-items: center; gap: 6px; }
.lvlup-landing .cp-table .cp-no { color: var(--ink-faint); font-weight: 500; }
.lvlup-landing .cp-table .cp-row-hi td:nth-child(2) { background: oklch(0.88 0.18 120 / 0.06); color: var(--ink); font-weight: 600; }
@media (max-width: 720px) {
  .lvlup-landing .cp-table thead th, .lvlup-landing .cp-table tbody td { padding: 12px 14px; font-size: 13px; }
}

/* FAQs */
.lvlup-landing .cp-faqs { max-width: 820px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
.lvlup-landing .cp-faq {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 14px; padding: 22px 24px;
}
.lvlup-landing .cp-faq h3 { font-family: var(--display); font-size: 16px; font-weight: 600; letter-spacing: -0.2px; margin: 0 0 8px; color: var(--ink); }
.lvlup-landing .cp-faq p { font-size: 14px; color: var(--ink-dim); margin: 0; line-height: 1.6; text-wrap: pretty; }

/* CTA */
.lvlup-landing .cp-cta {
  margin: 60px auto;
  max-width: 980px;
  background: var(--accent); color: var(--accent-ink);
  border-radius: 22px; padding: 40px 44px;
  display: flex; gap: 24px; align-items: center; justify-content: space-between;
  position: relative; overflow: hidden;
}
.lvlup-landing .cp-cta::before {
  content: ''; position: absolute; top: -50px; right: -50px;
  width: 240px; height: 240px;
  background: repeating-linear-gradient(45deg, transparent 0 10px, rgba(0,0,0,0.05) 10px 11px);
  border-radius: 50%;
}
.lvlup-landing .cp-cta-text { position: relative; }
.lvlup-landing .cp-cta-text h3 { font-family: var(--display); font-size: 26px; font-weight: 700; letter-spacing: -0.6px; margin: 0 0 6px; }
.lvlup-landing .cp-cta-text p { margin: 0; font-size: 15px; color: rgba(0,0,0,0.7); }
.lvlup-landing .cp-cta .lp-btn-primary { position: relative; background: var(--accent-ink); color: var(--accent); }
@media (max-width: 720px) { .lvlup-landing .cp-cta { flex-direction: column; align-items: flex-start; padding: 28px; } }
`;

function FeatureCell({ value }: { value: string | boolean }) {
  if (value === true)
    return (
      <span className="cp-yes">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Yes
      </span>
    );
  if (value === false) return <span className="cp-no">—</span>;
  return <span>{value}</span>;
}

export function CompareHero({ target }: { target: CompareTarget }) {
  return (
    <section className="cp-hero">
      <div className="cp-hero-grid" />
      <div className="cp-hero-glow" />
      <div className="lp-container cp-hero-inner">
        <span className="cp-vs">
          <strong>LVLUP</strong>
          <span className="cp-vs-x">vs</span>
          <strong>{target.displayName}</strong>
        </span>
        <h1>{target.heroHeadline}</h1>
        <p className="cp-lead">{target.heroSub}</p>
        <div className="cp-hero-ctas">
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lp-btn lp-btn-primary"
          >
            Start 14-day free trial
          </a>
          <a href="/discover" className="lp-btn lp-btn-ghost">
            Book a discovery call
          </a>
        </div>
      </div>
    </section>
  );
}

export function CompareReasons({ target }: { target: CompareTarget }) {
  return (
    <section className="cp-section">
      <div className="lp-container">
        <div className="cp-section-head">
          <h2>
            Why coaches switch from {target.displayName} to{" "}
            <span className="lp-accent">LVLUP</span>
          </h2>
          <p>{target.positioning}</p>
        </div>
        <div className="cp-reasons">
          {target.switchReasons.map((r) => (
            <div key={r.title} className="cp-reason">
              <h3>{r.title}</h3>
              <p>{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CompareTable({ target }: { target: CompareTarget }) {
  return (
    <section className="cp-section">
      <div className="lp-container">
        <div className="cp-section-head">
          <h2>LVLUP vs {target.displayName} — feature by feature</h2>
          <p>{target.pricingNote}</p>
        </div>
        <div className="cp-table-wrap">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="cp-th-us">LVLUP</th>
                <th>{target.displayName}</th>
              </tr>
            </thead>
            <tbody>
              {target.features.map((row: CompareFeatureRow) => (
                <tr
                  key={row.label}
                  className={row.highlight ? "cp-row-hi" : undefined}
                >
                  <td>{row.label}</td>
                  <td>
                    <FeatureCell value={row.lvlup} />
                  </td>
                  <td>
                    <FeatureCell value={row.competitor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function CompareFAQs({ target }: { target: CompareTarget }) {
  return (
    <section className="cp-section">
      <div className="lp-container">
        <div className="cp-section-head">
          <h2>{target.displayName} alternative — FAQ</h2>
        </div>
        <div className="cp-faqs">
          {target.faqs.map((f) => (
            <div key={f.q} className="cp-faq">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CompareCTA({ target }: { target: CompareTarget }) {
  return (
    <div className="lp-container">
      <div className="cp-cta">
        <div className="cp-cta-text">
          <h3>Ready to switch from {target.displayName}?</h3>
          <p>
            Start your 14-day free trial and have a branded app proposal in your
            inbox within 24 hours.
          </p>
        </div>
        <a
          href={REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="lp-btn lp-btn-primary"
        >
          Start free trial
        </a>
      </div>
    </div>
  );
}

export function CompareCrossLinks({ currentSlug }: { currentSlug: string }) {
  const others = COMPARE_TARGETS.filter((t) => t.slug !== currentSlug);
  return (
    <section className="cp-section">
      <div className="lp-container">
        <div className="cp-section-head">
          <h2>Compare LVLUP to other coaching platforms</h2>
        </div>
        <div className="cp-hub-grid">
          {others.map((t) => (
            <a key={t.slug} href={`/compare/${t.slug}`} className="cp-hub-card">
              <span className="cp-hub-tag">LVLUP vs {t.displayName}</span>
              <h3>{t.tagline}</h3>
              <span className="cp-hub-cta">
                Read comparison
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CompareShell({
  children,
  current,
}: {
  children: React.ReactNode;
  current?: undefined;
}) {
  return (
    <div className="lvlup-landing">
      <style
        dangerouslySetInnerHTML={{ __html: BASE_STYLES + COMPARE_STYLES }}
      />
      <SiteNav current={current} />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}
